import { motion } from "framer-motion";
import { MapPin, MessageCircle, ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import KineticLines from "@/components/KineticLines";
import LeadForm from "@/components/LeadForm";
import { WHATSAPP_DISPLAY, WHATSAPP_LINK } from "@/lib/constants";

const Contato = () => (
    <main>
        <Seo
            title="Contato — Solicite um Diagnóstico | Vorantis Soluções"
            description="Fale com a Vorantis Soluções em Barra Mansa/RJ. Solicite um diagnóstico estratégico de gestão e posicionamento digital para sua empresa."
        />

        <section className="grain-overlay hero-grid-bg relative overflow-hidden bg-navy-deep pb-24 pt-40 md:pb-28 md:pt-48" data-testid="contato-hero">
            <div className="pointer-events-none absolute -left-40 top-1/4 h-[420px] w-[420px] rounded-full bg-corp/20 blur-[140px]" />
            <div className="relative mx-auto max-w-7xl px-6 md:px-10">
                <motion.div
                    className="flex items-center gap-3"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.15 }}
                >
                    <span className="h-px w-10 bg-emerald-400" />
                    <span className="font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-emerald-400">Vamos conversar</span>
                </motion.div>
                <h1 className="mt-8 max-w-3xl font-display text-4xl font-extrabold leading-[1.04] tracking-tight text-white sm:text-6xl">
                    <KineticLines
                        testid="contato-headline"
                        delay={0.3}
                        lines={["O próximo movimento", <>começa com uma <span className="text-emerald-400">conversa.</span></>]}
                    />
                </h1>
                <motion.p
                    className="mt-7 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.85 }}
                >
                    Cada empresa tem desafios, prioridades e oportunidades diferentes. Vamos entender
                    o seu cenário e construir uma proposta adequada à sua realidade.
                </motion.p>
            </div>
        </section>

        <section className="bg-slate-50 py-20 md:py-28" data-testid="contato-form-section">
            <div className="mx-auto grid max-w-7xl gap-10 px-6 md:px-10 lg:grid-cols-12">
                <Reveal className="lg:col-span-7">
                    <LeadForm origem="pagina-contato" />
                </Reveal>
                <div className="lg:col-span-5">
                    <Reveal delay={0.12}>
                        <div className="flex h-full flex-col gap-6">
                            <div className="rounded-3xl bg-navy-deep p-9 text-white md:p-10">
                                <p className="font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-emerald-400">
                                    Contato direto
                                </p>
                                <a
                                    href={WHATSAPP_LINK}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="contato-whatsapp-button"
                                    className="mt-6 flex items-center justify-between gap-4 rounded-2xl bg-emerald-500 px-6 py-5 font-semibold text-white transition-colors hover:bg-emerald-600"
                                >
                                    <span className="flex items-center gap-3">
                                        <MessageCircle size={22} />
                                        WhatsApp
                                    </span>
                                    <span className="text-sm">{WHATSAPP_DISPLAY}</span>
                                </a>
                                <div className="mt-8 flex items-start gap-3 text-sm text-slate-300">
                                    <MapPin size={18} className="mt-0.5 shrink-0 text-emerald-400" />
                                    <div>
                                        <p className="font-semibold text-white">Barra Mansa • RJ</p>
                                        <p className="mt-1 text-slate-400">Atendimento em todo o Sul Fluminense</p>
                                    </div>
                                </div>
                            </div>
                            <div className="rounded-3xl border border-slate-200 bg-white p-8">
                                <div className="flex items-start gap-3">
                                    <ShieldCheck size={20} className="mt-0.5 shrink-0 text-emerald-600" />
                                    <p className="text-xs leading-relaxed text-slate-500">
                                        Os dados enviados por este formulário são utilizados
                                        exclusivamente para responder ao seu contato e elaborar uma
                                        proposta, conforme a LGPD (Lei nº 13.709/2018). Não
                                        compartilhamos suas informações com terceiros.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    </main>
);

export default Contato;
